import React from 'react';
import { Box, Typography, TextField, FormControlLabel, Switch, Divider, Stack, Alert, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { neutral } from '_lex/_foundations/phxColors';
import { TemplateSection, Template } from '../../_types';
import { isSectionLockedByParent } from '../../_utils/sectionComparison';
import { translate as t } from 'hooks/useTranslations';
import { useTemplateBuilderContext } from '../context';

interface SectionPropertiesPanelProps {
  section: TemplateSection;
  parentTemplate?: Template | null;
  onTitleChange: (sectionId: number, title: string) => void;
  onDescriptionChange: (sectionId: number, description: string) => void;
  onRequiredChange: (sectionId: number, required: boolean) => void;
  onLockedChange: (sectionId: number, locked: boolean) => void;
  onRepeatableChange: (sectionId: number, repeatable: boolean) => void;
}

const SectionPropertiesPanel: React.FC<SectionPropertiesPanelProps> = ({
  section,
  parentTemplate,
  onTitleChange,
  onDescriptionChange,
  onRequiredChange,
  onLockedChange,
  onRepeatableChange,
}) => {
  const { isLexipolTemplate } = useTemplateBuilderContext();
  const templateMode = isLexipolTemplate ? 'lexipol' : 'agency';

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onTitleChange(section.id, event.target.value);
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onDescriptionChange(section.id, event.target.value);
  };

  const handleRequiredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRequiredChange(section.id, event.target.checked);
  };

  const handleLockedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onLockedChange(section.id, event.target.checked);
  };

  const handleRepeatableChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRepeatableChange(section.id, event.target.checked);
  };

  // Use effective values for display
  const displayName = section.effectiveName || section.name || '';
  const displayDescription = section.effectiveDescription || section.description || '';

  // Check if section is locked by parent template
  const isLockedByParent = isSectionLockedByParent(section, parentTemplate);

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 2, maxWidth: '100%' }}>
      <Stack spacing={3}>
        {/* Yellow banner for locked sections */}
        {isLockedByParent && (
          <Alert
            severity='warning'
            icon={<LockIcon fontSize='small' />}
            sx={{
              '& .MuiAlert-icon': {
                color: '#ed6c02',
              },
              '& .MuiAlert-message': {
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'normal',
              },
            }}
          >
            {t(`performanceReporting.templates.builder.${templateMode}.lockedSection.banner`)}
          </Alert>
        )}
        {/* Section title */}
        <Box>
          <Typography
            variant='body2'
            fontWeight='600'
            color='text.primary'
            sx={{ mb: 1 }}
          >
            Section title
          </Typography>
          <TextField
            fullWidth
            size='small'
            value={displayName}
            onChange={handleTitleChange}
            variant='outlined'
            disabled={isLockedByParent}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                fontSize: '0.875rem',
              },
            }}
          />
        </Box>

        {/* Section description */}
        <Box>
          <Typography
            variant='body2'
            fontWeight='600'
            color='text.primary'
            sx={{ mb: 1 }}
          >
            Section description
          </Typography>
          <TextField
            fullWidth
            size='small'
            multiline
            rows={3}
            value={displayDescription}
            onChange={handleDescriptionChange}
            variant='outlined'
            placeholder='This is an example of a description'
            disabled={isLockedByParent}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                fontSize: '0.875rem',
              },
            }}
          />
          <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto',
                whiteSpace: 'normal',
                display: 'block',
                lineHeight: 1.4,
              }}
            >
              Will be displayed below the section title to help users understand the purpose of this section.
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Required section toggle */}
        <Box>
          <Tooltip
            title={t(`performanceReporting.templates.builder.${templateMode}.lockedSection.inheritedAttribute`)}
            disableHoverListener={!isLockedByParent}
            placement='top'
          >
            <FormControlLabel
              control={
                <Switch
                  checked={section.effectiveIsRequired || false}
                  onChange={handleRequiredChange}
                  size='small'
                  disabled={isLockedByParent}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4caf50', // Green color like the rest of the app
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#4caf50', // Green track when enabled
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography
                    variant='body2'
                    fontWeight='600'
                    color='text.primary'
                  >
                    Required section
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      hyphens: 'auto',
                    }}
                  >
                    Marks the section as required in templates.
                  </Typography>
                </Box>
              }
              labelPlacement='start'
              sx={{
                m: 0,
                width: '100%',
                justifyContent: 'space-between',
                gap: 2,
                '& .MuiFormControlLabel-label': {
                  flex: 1,
                  maxWidth: 'calc(100% - 80px)',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  marginRight: 2,
                },
              }}
            />
          </Tooltip>
        </Box>

        {/* Locked section toggle - only show for Lexipol templates */}
        {isLexipolTemplate && (
          <Box>
            <Tooltip
              title={t(`performanceReporting.templates.builder.${templateMode}.lockedSection.inheritedAttribute`)}
              disableHoverListener={!isLockedByParent}
              placement='top'
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={section.effectiveIsLocked || false}
                    onChange={handleLockedChange}
                    size='small'
                    disabled={isLockedByParent}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#4caf50', // Green color like the rest of the app
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#4caf50', // Green track when enabled
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography
                      variant='body2'
                      fontWeight='600'
                      color='text.primary'
                    >
                      Locked section
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto',
                      }}
                    >
                      Locks section and fields from agency edits.
                    </Typography>
                  </Box>
                }
                labelPlacement='start'
                sx={{
                  m: 0,
                  width: '100%',
                  justifyContent: 'space-between',
                  gap: 2,
                  '& .MuiFormControlLabel-label': {
                    flex: 1,
                    maxWidth: 'calc(100% - 80px)',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    marginRight: 2,
                  },
                }}
              />
            </Tooltip>
          </Box>
        )}

        {/* Repeatable section toggle */}
        <Box>
          <Tooltip
            title={t(`performanceReporting.templates.builder.${templateMode}.lockedSection.inheritedAttribute`)}
            disableHoverListener={!isLockedByParent}
            placement='top'
          >
            <FormControlLabel
              control={
                <Switch
                  checked={section.effectiveIsRepeatable || false}
                  onChange={handleRepeatableChange}
                  size='small'
                  disabled={isLockedByParent}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4caf50', // Green color like the rest of the app
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#4caf50', // Green track when enabled
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography
                    variant='body2'
                    fontWeight='600'
                    color='text.primary'
                  >
                    Repeatable section
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      hyphens: 'auto',
                    }}
                  >
                    Section can be repeated during data entry.
                  </Typography>
                </Box>
              }
              labelPlacement='start'
              sx={{
                m: 0,
                width: '100%',
                justifyContent: 'space-between',
                gap: 2,
                '& .MuiFormControlLabel-label': {
                  flex: 1,
                  maxWidth: 'calc(100% - 80px)',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  marginRight: 2,
                },
              }}
            />
          </Tooltip>
        </Box>
      </Stack>
    </Box>
  );
};

export default SectionPropertiesPanel;
